import type { Board } from "../board";
import { getMobGoldValue } from "../economy/mobGoldValues";
import { DamageType } from "../damageType";
import { BallisticMovementEffect } from "../effects/ballisticMovementEffect";
import type { SomethingWithState } from "../interfaces";
import type { PhysicalThing } from "../physicalThing";
import { STD_BASE_HP } from "../physicalThingWithHp";
import { deserializeXY } from "../serialization";
import { Sound } from "../things/sounds/sound";
import { SoundId } from "../things/sounds/soundId";
import { ParticleType } from "../particleType";
import { WeakMob } from "../weakMob";
import { BasicAI } from "../ai/basicAI";
import type { XY } from "../xy";
import { BiterCorpse } from "./biterCorpse";
import { SolidifiedBiter } from "../things/solidifiedBiter";

export enum BiterState {
	NORMAL = "NORMAL",
	HURT = "HURT",
}

export class Biter extends WeakMob implements SomethingWithState {
	static key = "Biter";
	private ai: BasicAI;

	constructor(xy: XY) {
		super(xy);
		this.hp = STD_BASE_HP * 2;
		this.maxHp = STD_BASE_HP * 2;
		this.damageMultipliers.set(DamageType.BUBBLE, 0.5);
		this.damageMultipliers.set(DamageType.MELEE, 0.5);
		this.ai = new BasicAI(this);
		this.ai.attackSoundSupplier = () => new Sound(SoundId.MOB_PHISICAL_ATTACK, this.xy);
	}

	override getBloodParticleType(): ParticleType {
		return ParticleType.BLUE_BLOOD;
	}

	override getBaseGoldValue(): number {
		return getMobGoldValue(Biter.key);
	}

	getState(): BiterState {
		return this.hp < this.maxHp ? BiterState.HURT : BiterState.NORMAL;
	}

	createSolidifiedVariant(): PhysicalThing | null {
		return new SolidifiedBiter(this.xy);
	}

	generateCorpse(): PhysicalThing | null {
		return new BiterCorpse(this.xy);
	}

	override moveTo(xy: XY): void {
		if (!this.movementCadence.isReady()) return;
		const oldXY = this.xy;
		this.xy = xy;
		this.board!.addEffect(new BallisticMovementEffect(this, oldXY, xy));
	}

	processMobAction(_board: Board): void {
		this.ai.processTurn();
	}

	static deserialize(data: any): Biter {
		const m = new Biter(deserializeXY(data.xy));
		m.restoreMobFields(data);
		return m;
	}
}
